import { useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

import { AnimationDurations } from "../../../theme/animations";
import { useModalTransition } from "./ModalTransitionContext";

const DISMISS_FALLBACK_MS = AnimationDurations.moderate02 + 50;

export type UseCoordinatedNativeModalResult = {
  /** Pass to React Native `Modal` `visible`. */
  rnModalVisible: boolean;
  /** iOS only: pass to `Modal` `onDismiss` so the coordinator can finish the dismiss sequence. */
  onDismiss: (() => void) | undefined;
};

/**
 * Bridges logical `visible` to React Native `Modal` `visible` using
 * `ModalTransitionProvider` so a second modal does not present while another
 * is still dismissing (transparent modals / iOS).
 *
 * @see https://github.com/facebook/react-native/issues/29929
 */
export const useCoordinatedNativeModal = (
  visible: boolean
): UseCoordinatedNativeModalResult => {
  const {
    notifyDismissStarted,
    notifyDismissCompleted,
    runAfterIdle,
    isDismissInFlight,
  } = useModalTransition();

  const [rnModalVisible, setRnModalVisible] = useState(visible);
  const completionPendingRef = useRef(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current != null) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const completeDismissIfPending = useCallback(() => {
    clearFallbackTimer();
    if (completionPendingRef.current) {
      completionPendingRef.current = false;
      notifyDismissCompleted();
    }
  }, [clearFallbackTimer, notifyDismissCompleted]);

  const scheduleFallbackDismissComplete = useCallback(() => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      completeDismissIfPending();
    }, DISMISS_FALLBACK_MS);
  }, [clearFallbackTimer, completeDismissIfPending]);

  useEffect(() => {
    if (visible) {
      let cancelled = false;
      const open = () => {
        if (!cancelled) {
          setRnModalVisible(true);
        }
      };
      if (!isDismissInFlight()) {
        open();
      } else {
        runAfterIdle(open);
      }
      return () => {
        cancelled = true;
        clearFallbackTimer();
        if (completionPendingRef.current) {
          completeDismissIfPending();
        }
      };
    }

    setRnModalVisible((wasVisible) => {
      if (wasVisible) {
        completionPendingRef.current = true;
        notifyDismissStarted();
        scheduleFallbackDismissComplete();
      }
      return false;
    });

    return () => {
      clearFallbackTimer();
      if (completionPendingRef.current) {
        completeDismissIfPending();
      }
    };
  }, [
    visible,
    runAfterIdle,
    isDismissInFlight,
    notifyDismissStarted,
    scheduleFallbackDismissComplete,
    clearFallbackTimer,
    completeDismissIfPending,
  ]);

  const onDismiss =
    Platform.OS === "ios"
      ? () => {
          completeDismissIfPending();
        }
      : undefined;

  return { rnModalVisible, onDismiss };
};
